import React from 'react';
import config from 'config';
import { History } from 'history';
import { useParams } from 'react-router-dom';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { ICourse } from 'types/types';
import { useCourseApi } from 'api/course/useCourseApi';
import { CourseDetailsComponent } from 'containers/user-interface/Public/CourseDetails';
import { parseIdFromSeoname } from 'helpers/commonUtils';
import { sendEvent } from 'vendor-deprecated/Event';
import { openNotification } from 'vendor-deprecated/Notification';
import { useTranslationText } from 'locale';
import { useBuyProducts } from 'api/rest/hooks/buyProducts';
import { useReviewApi } from 'api/review/useReviewApi';
import { useReview } from './hook/useReview';
import { useSession } from 'session/useSession';
import { useUserApi } from 'api/user/useUserApi';

type ContainerProps = {
  history: History;
};

const stripePromise: Promise<Stripe | null> = loadStripe(config.STRIPE_PUBLISH);

export const CourseDetailsContainer = (props: ContainerProps) => {
  const { session, reauthenticate } = useSession();
  const { seoname, token } = useParams<{ seoname: string; token: string }>();

  const courseId = parseIdFromSeoname(seoname);

  const [loading, setLoading] = React.useState(true);
  const [checkToken, setCheckToken] = React.useState(false);
  const [isCheckout, setIsCheckout] = React.useState(false);
  const { get: getCourseApi } = useCourseApi();
  const {
    getUserCourse: { asyncGetUserCourse },
  } = useUserApi();
  const { translation } = useTranslationText();
  const { buyProducts, loading: addCourseFreeLoading } = useBuyProducts();
  const { create: createReviewApi, update: updateReviewApi } = useReviewApi();

  /**
   * Check valid review
   */
  const { isValidReview, updateCacheLastTimeReview } = useReview(courseId);

  /**
   * Check token and re-authenticate
   */
  const handleCheckToken = async () => {
    if (checkToken) {
      return;
    }

    if (token) {
      localStorage.setItem('QHC_LOGIN_TOKEN', token);
      await reauthenticate();
      setCheckToken(true);
    } else {
      setCheckToken(true);
    }
  };

  /**
   * Add course to my course list if course is free
   */
  const handleAddFreeCourse = async () => {
    if (!session.user?.id) {
      props.history.push('/login');
      return;
    }

    const isSuccess = await buyProducts({
      priceIds: [],
      courseFreeId: getCourseApi.course?.id,
    });

    if (isSuccess) {
      if (getCourseApi.course?.type === 'COURSE') {
        const userCouse = await asyncGetUserCourse({ userId: session.user.id, courseId: getCourseApi.course?.id });
        if (userCouse?.books && userCouse?.books.length) {
          props.history.push(`/user/lecture/${userCouse?.books[0]?.id}`);
        } else {
          props.history.push(`/user/courses`);
        }
      } else {
        props.history.push(`/user/courses/${getCourseApi.course?.id}/exams`);
      }
    } else {
      openNotification({ type: 'error', message: translation('message.errorMessage1') });
    }
  };

  /**
   * Add course to my cart
   */
  const handleAddToCart = async () => {
    const data = localStorage.getItem('cartIds') || '{}';
    let cart: { [key: string]: any } = {};
    try {
      cart = JSON.parse(data);
    } catch (error) {}
    cart[courseId!] = getCourseApi.course;

    localStorage.setItem('cartIds', JSON.stringify(cart));

    sendEvent({ type: 'update-cart' });

    openNotification({ type: 'success', message: translation('public.courseDetails.addToCartSuccess') });
  };

  /**
   * Action payment
   */
  const handlePayment = React.useCallback(async () => {
    const data = localStorage.getItem('cartIds') || '{}';
    let cart: { [key: string]: any } = {};
    try {
      cart = JSON.parse(data);
    } catch (error) {}
    cart[courseId!] = getCourseApi.course;

    localStorage.setItem('cartIds', JSON.stringify(cart));

    sendEvent({ type: 'update-cart' });

    if (!session.user?.id) {
      props.history.push('/login');
    } else {
      props.history.push('/my-cart');
    }
  }, [session, getCourseApi.course, courseId]);

  const handleCreateReview = async (rate: number, content: string) => {
    const results = await createReviewApi.createReview({ rate, content, courseId });

    if (!results.error) {
      getCourseApi.getCourse({ id: courseId! });
      updateCacheLastTimeReview();
    }
  };

  const handleEditReview = async (id: string, rate: number, content: string) => {
    const results = await updateReviewApi.updateReview({ id, rate, content });
    if (!results.error) {
      getCourseApi.getCourse({ id: courseId! });
    }
  };

  /**
   * Effect get course details
   */
  React.useEffect(() => {
    if (courseId) {
      getCourseApi.getCourse({ id: courseId });
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  /**
   * Effect get token
   * Action: open course details from mobile app
   */
  React.useEffect(() => {
    handleCheckToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /**
   * Effect Check automatic add to my cart
   * Action: open course details from mobile app
   */
  React.useEffect(() => {
    if (checkToken && getCourseApi.course && token && token.length > 0 && session.isAuthenticated) {
      if (getCourseApi.course.isFree) {
        handleAddFreeCourse();
      } else {
        handlePayment();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCourseApi.course, session, checkToken, token]);

  return (
    <Elements stripe={stripePromise}>
      <CourseDetailsComponent
        auth={session.user}
        isValidReview={isValidReview}
        addCourseFreeLoading={addCourseFreeLoading}
        loading={loading || getCourseApi.loading || !checkToken}
        error={getCourseApi.error}
        history={props.history}
        course={getCourseApi.course || ({} as ICourse)}
        isCheckout={isCheckout}
        closeCheckout={() => {
          setIsCheckout(false);
        }}
        onAddToCart={handleAddToCart}
        onBuyNow={handlePayment}
        onAddToMyCourse={handleAddFreeCourse}
        onCreateReview={handleCreateReview}
        onEditReview={handleEditReview}
      />
    </Elements>
  );
};

export default CourseDetailsContainer;
